import { Box } from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { ImageDropzone } from "components/ImageDropZone/ImageDropzone"
import jsQR from "jsqr"
import { enqueueSnackbar } from "notistack"
import { JSX, useEffect, useRef, useState } from "react"
import imageDataToBase64 from "utils/imageDataToBase64"

type CameraProps = {
  label: string
  onQRCodeScan?: (data: string) => void
  onCapture?: (image: string) => void
}

const Camera = (props: CameraProps): JSX.Element => {
  const { onQRCodeScan, onCapture, label } = props

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [cameraAvailable, setCameraAvailable] = useState(true)
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        })
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (error) {
        setCameraAvailable(false)
      }
    }
    startCamera()
    const lastStream = videoRef.current
      ? (videoRef.current.srcObject as MediaStream)
      : null
    return () => {
      if (lastStream) {
        lastStream.getTracks().forEach((track) => track.stop())
      }
    }
  }, [])

  const handleCapture = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!canvas || !video) return

    const context = canvas.getContext("2d")
    if (!context) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const x = 0
    const y = 0

    context.drawImage(video, x, y, canvas.width, canvas.height)

    const imageData = context.getImageData(x, y, canvas.width, canvas.height)
    if (onQRCodeScan) {
      const qrCode = jsQR(imageData.data, canvas.width, canvas.height)
      if (qrCode) {
        enqueueSnackbar("QR-Code gefunden", { variant: "success" })
        onQRCodeScan(qrCode.data)
      } else {
        enqueueSnackbar("Kein QR-Code gefunden", { variant: "info" })
      }
    }
    if (onCapture) {
      onCapture(imageDataToBase64(imageData))
    }
  }

  const onImageFileChosen = (files: File[]) => {
    if (onCapture) {
      const file = files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        onCapture(result)
      }
      reader.readAsDataURL(file)
    }
  }

  const onFileReject = (rejections: any) => {
    console.error("File rejected", rejections)
  }

  return (
    <>
      {cameraAvailable ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 500,
              borderRadius: "8px",
            }}
          >
            <Box>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  borderRadius: "8px",
                }}
              ></video>
              <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
            </Box>
          </Box>
          <StyledButton
            buttonType="custom"
            customText={label}
            fullWidth
            variant="contained"
            onClick={handleCapture}
          />
          {!onQRCodeScan && (
            <StyledButton
              buttonType="custom"
              customText="Foto hochladen"
              variant="outlined"
              fullWidth
              onClick={() => setCameraAvailable(false)}
            />
          )}
        </Box>
      ) : (
        <>
          {onCapture && (
            <ImageDropzone
              onImageFileChosen={onImageFileChosen}
              onFileReject={onFileReject}
              setImage={() => {}}
            />
          )}
        </>
      )}
    </>
  )
}

export default Camera
